import React, { useState } from 'react';
import styles from './CreditPackages.module.css';

function fmt(n) { return `$${Number(n || 0).toFixed(2)}`; }

export default function CreditPackages({ config, refresh, showToast, API, authHeader }) {
  const packages = config?.creditPackages || [];

  const [editingId, setEditingId] = useState(null); // id of pack being edited
  const [editData, setEditData]   = useState({});
  const [showAdd, setShowAdd]     = useState(false);
  const [newPack, setNewPack]     = useState({ label: '', amount: '', description: '' });
  const [saving, setSaving]       = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Persist the whole array — the backend normalizes fields + assigns ids to
  // any new packages. Returns true on success so callers can close their form.
  const saveAll = async (nextPackages, successMsg) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/config/credit-packages`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ creditPackages: nextPackages }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); showToast(e.error || 'Save failed', 'error'); return false; }
      if (successMsg) showToast(successMsg);
      refresh();
      return true;
    } catch { showToast('Network error', 'error'); return false; }
    finally { setSaving(false); }
  };

  const startEdit = (p) => { setEditingId(p.id); setEditData({ ...p }); setShowAdd(false); setConfirmDelete(false); };
  const cancelEdit = () => { setEditingId(null); setEditData({}); setConfirmDelete(false); };

  const handleSaveEdit = async () => {
    if (!editData.label || editData.amount === '' || editData.amount == null) {
      showToast('Name and amount are required', 'error'); return;
    }
    const next = packages.map(p => p.id === editingId
      ? { ...p, label: editData.label, amount: parseFloat(editData.amount), description: editData.description || '', active: editData.active !== false }
      : p);
    if (await saveAll(next, `${editData.label} updated`)) cancelEdit();
  };

  const handleAdd = async () => {
    if (!newPack.label || newPack.amount === '') { showToast('Name and amount are required', 'error'); return; }
    const next = [...packages, { label: newPack.label, amount: parseFloat(newPack.amount), description: newPack.description || '', active: true }];
    if (await saveAll(next, `${newPack.label} added`)) {
      setShowAdd(false);
      setNewPack({ label: '', amount: '', description: '' });
    }
  };

  const toggleActive = async (p) => {
    const goingInactive = p.active !== false;
    const next = packages.map(x => x.id === p.id ? { ...x, active: !goingInactive } : x);
    await saveAll(next, `${p.label} ${goingInactive ? 'deactivated' : 'activated'}`);
  };

  const handleDelete = async () => {
    const target = packages.find(p => p.id === editingId);
    const next = packages.filter(p => p.id !== editingId);
    if (await saveAll(next, `${target?.label || 'Package'} deleted`)) cancelEdit();
  };

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <h1 className={styles.pageTitle}>Credit Packages</h1>
          <p className={styles.pageSubtitle}>Preset credit amounts parents can buy on the Parent Portal.</p>
        </div>
        <button className={styles.addBtn} onClick={() => { setShowAdd(true); setEditingId(null); }}>
          ＋ Add Package
        </button>
      </div>

      <div className={styles.grid}>
        {packages.map(p => (
          <div key={p.id} className={`${styles.card} ${p.active === false ? styles.inactive : ''}`}>
            {editingId === p.id ? (
              /* ── Edit mode ─────────────────────────────────────────────── */
              <div className={styles.editForm}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Package Name</label>
                  <input className={styles.fieldInput}
                    value={editData.label || ''}
                    onChange={e => setEditData(d => ({ ...d, label: e.target.value }))}
                    placeholder="e.g. Standard" />
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Amount</label>
                  <div className={styles.rateInput}>
                    <span className={styles.rateDollar}>$</span>
                    <input className={styles.fieldInput}
                      type="number" min="1" step="5"
                      value={editData.amount ?? ''}
                      onChange={e => setEditData(d => ({ ...d, amount: e.target.value }))}
                      placeholder="300" />
                  </div>
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Description (optional)</label>
                  <input className={styles.fieldInput}
                    value={editData.description || ''}
                    onChange={e => setEditData(d => ({ ...d, description: e.target.value }))}
                    placeholder="e.g. Most popular" />
                </div>
                <div className={styles.editActions}>
                  <button className={styles.cancelBtn} onClick={cancelEdit}>Cancel</button>
                  <button className={styles.saveBtn} disabled={saving} onClick={handleSaveEdit}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
                {!confirmDelete ? (
                  <button className={styles.deleteLink} onClick={() => setConfirmDelete(true)}>
                    Delete Package
                  </button>
                ) : (
                  <div className={styles.confirmDelete}>
                    <span className={styles.confirmText}>
                      Permanently delete “{editData.label || 'this package'}”? This can’t be undone.
                    </span>
                    <div className={styles.confirmActions}>
                      <button className={styles.cancelBtn} onClick={() => setConfirmDelete(false)}>Cancel</button>
                      <button className={styles.confirmDeleteBtn} disabled={saving} onClick={handleDelete}>
                        {saving ? 'Deleting...' : 'Confirm Delete'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* ── View mode ─────────────────────────────────────────────── */
              <>
                <div className={styles.cardHeader}>
                  <div className={styles.cardMeta}>
                    <div className={styles.cardLabel}>{p.label}</div>
                    {p.description && <div className={styles.cardDesc}>{p.description}</div>}
                  </div>
                  {p.active === false && <span className={styles.inactiveBadge}>Inactive</span>}
                </div>
                <div className={styles.cardAmount}>{fmt(p.amount)}</div>
                <div className={styles.cardActions}>
                  <button className={styles.editBtn} onClick={() => startEdit(p)}>✎ Edit</button>
                  <button
                    className={`${styles.toggleBtn} ${p.active === false ? styles.toggleInactive : styles.toggleActive}`}
                    onClick={() => toggleActive(p)}>
                    {p.active === false ? 'Activate' : 'Deactivate'}
                  </button>
                </div>
              </>
            )}
          </div>
        ))}

        {/* ── Add new card ───────────────────────────────────────────────── */}
        {showAdd && (
          <div className={`${styles.card} ${styles.addCard}`}>
            <div className={styles.addCardTitle}>New Package</div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Package Name</label>
              <input className={styles.fieldInput}
                value={newPack.label}
                onChange={e => setNewPack(d => ({ ...d, label: e.target.value }))}
                placeholder="e.g. Premium" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Amount</label>
              <div className={styles.rateInput}>
                <span className={styles.rateDollar}>$</span>
                <input className={styles.fieldInput}
                  type="number" min="1" step="5"
                  value={newPack.amount}
                  onChange={e => setNewPack(d => ({ ...d, amount: e.target.value }))}
                  placeholder="600" />
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Description (optional)</label>
              <input className={styles.fieldInput}
                value={newPack.description}
                onChange={e => setNewPack(d => ({ ...d, description: e.target.value }))}
                placeholder="e.g. Best value" />
            </div>
            <div className={styles.editActions}>
              <button className={styles.cancelBtn} onClick={() => setShowAdd(false)}>Cancel</button>
              <button className={styles.saveBtn} disabled={saving} onClick={handleAdd}>
                {saving ? 'Adding...' : 'Add Package'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={styles.note}>
        <strong>Note:</strong> These packages appear on the Parent Portal's Purchase Credits page.
        Deactivating one hides it from parents without affecting past purchases; parents can always
        enter a custom amount.
      </div>
    </div>
  );
}
